import {
	isForbiddenApiError,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { createAppQueryCache } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { apiWrapperSetup } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { memo, useEffect, useState } from 'react';

const ApiLayer = ({ children }) => {
	const toastApi = useDSToast();
	const tDefault = useTranslationDefault();

	const resolveErrorMessage = (error: unknown) => {
		if (isForbiddenApiError(error)) {
			return tDefault('common_api_wrapper.forbidden.message', {
				defaultValue: "You don't have permission to access this resource.",
			});
		}

		return isStandardBackendError(error)
			? tDefault(`error_codes.${error.error?.code}`)
			: tDefault(`common_api_wrapper.local.message`);
	};

	const onError = async (error: unknown) => {
		// eslint-disable-next-line no-console
		console.error(error);

		toastApi.error({ message: resolveErrorMessage(error) });
	};

	const [queryClient] = useState(
		() =>
			new QueryClient({
				// v5-style cache-level error handling for queries; per-query intent
				// (suppress / custom message) is driven by `meta`.
				queryCache: createAppQueryCache({
					showErrorToast: (message) => toastApi.error({ message }),
					resolveGenericMessage: resolveErrorMessage,
				}),
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						refetchOnReconnect: false,
						retry: false,
						retryOnMount: false,
						initialData: undefined,
						gcTime: 0,
					},
					mutations: {
						retry: false,
						onError,
					},
				},
			})
	);

	useEffect(() => {
		apiWrapperSetup({
			commonLocalErrorHandler: onError,
		});
	}, []);

	return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
const Memoized = memo(ApiLayer);

export { Memoized as ApiLayer };
